/**
 * Parameter Uncertainty Propagation for Weibull Forecasting
 * 
 * This module handles the propagation of MLE parameter uncertainty through
 * forecast calculations to produce more realistic confidence intervals.
 */

import { MLEResult } from './weibullMLE';

/**
 * Structured representation of Weibull parameter uncertainty
 */
export interface WeibullParameterUncertainty {
  shape: {
    estimate: number;
    standardError: number;
    lowerCI: number;
    upperCI: number;
  };
  scale: {
    estimate: number;
    standardError: number;
    lowerCI: number;
    upperCI: number;
  };
  covariance: {
    matrix: number[][];  // 2x2 covariance matrix
    correlation: number; // correlation coefficient between shape and scale
  };
  method: 'Fisher' | 'ProfileLikelihood';
  confidenceLevel: number;
  sampleSize: number; // Number of observations used in MLE
}

/**
 * Invert 2x2 matrix (same as in weibullMLE.ts but exported for reuse)
 */
export function invertMatrix2x2(matrix: number[][]): number[][] {
  const [[a, b], [c, d]] = matrix;
  const det = a * d - b * c;
  
  if (Math.abs(det) < 1e-12) {
    throw new Error('Singular matrix - cannot invert');
  }
  
  return [
    [d / det, -b / det],
    [-c / det, a / det]
  ];
}

/**
 * Extract parameter uncertainty from MLE results
 * 
 * @param mleResult - Result from MLE estimation
 * @returns Structured parameter uncertainty information
 */
export function extractParameterUncertainty(mleResult: MLEResult): WeibullParameterUncertainty | null {
  // Check if we have the necessary information
  if (!mleResult.standardErrors) {
    console.warn('MLE result missing standard errors');
    return null;
  }
  
  // Check if Fisher Information Matrix is valid
  const hasFisherInfo = mleResult.fisherInformation && mleResult.fisherInformation.determinant > 0;
  
  if (!hasFisherInfo && (!mleResult.confidenceIntervals || mleResult.confidenceIntervals.method !== 'Profile Likelihood')) {
    console.warn('Neither valid Fisher Information nor Profile Likelihood confidence intervals available');
    return null;
  }
  
  // Get confidence interval information
  const confidenceLevel = mleResult.confidenceIntervals ? 0.95 : 0.95; // Default to 95%
  const method = (mleResult.confidenceIntervals?.method || 'Fisher') as 'Fisher' | 'ProfileLikelihood';
  
  let covMatrix: number[][];
  let correlation: number;
  
  try {
    if (hasFisherInfo) {
      // Use Fisher Information for covariance (preferred method)
      covMatrix = invertMatrix2x2(mleResult.fisherInformation.matrix);
      
      // Calculate correlation coefficient
      const varShape = covMatrix[0][0];
      const varScale = covMatrix[1][1];
      const covShapeScale = covMatrix[0][1];
      
      correlation = covShapeScale / (Math.sqrt(varShape) * Math.sqrt(varScale));
    } else {
      if (!mleResult.confidenceIntervals) {
        throw new Error('Profile Likelihood confidence intervals not available');
      }
      
      // Approximate standard errors from confidence interval widths
      // For 95% CI: width ≈ 2 * 1.96 * SE, so SE ≈ width / (2 * 1.96)
      const zCritical = 1.96; // For 95% CI
      
      const shapeWidth = mleResult.confidenceIntervals.shape.upper - mleResult.confidenceIntervals.shape.lower;
      const scaleWidth = mleResult.confidenceIntervals.scale.upper - mleResult.confidenceIntervals.scale.lower;
      
      const approxShapeSE = shapeWidth / (2 * zCritical);
      const approxScaleSE = scaleWidth / (2 * zCritical);
      
      // Create diagonal covariance matrix (assume independence for Profile Likelihood)
      // This is a conservative approximation - Profile Likelihood doesn't directly give correlation
      covMatrix = [
        [approxShapeSE * approxShapeSE, 0], // Assume zero covariance
        [0, approxScaleSE * approxScaleSE]
      ];
      
      correlation = 0; // Conservative assumption of independence
    }
    
    // Use appropriate standard errors based on method
    const shapeStdErr = hasFisherInfo ? mleResult.standardErrors.shape : Math.sqrt(covMatrix[0][0]);
    const scaleStdErr = hasFisherInfo ? mleResult.standardErrors.scale : Math.sqrt(covMatrix[1][1]);
    
    return {
      shape: {
        estimate: mleResult.parameters.shape,
        standardError: shapeStdErr,
        lowerCI: mleResult.confidenceIntervals?.shape.lower || 
                 mleResult.parameters.shape - 1.96 * shapeStdErr,
        upperCI: mleResult.confidenceIntervals?.shape.upper || 
                 mleResult.parameters.shape + 1.96 * shapeStdErr
      },
      scale: {
        estimate: mleResult.parameters.scale,
        standardError: scaleStdErr,
        lowerCI: mleResult.confidenceIntervals?.scale.lower || 
                 mleResult.parameters.scale - 1.96 * scaleStdErr,
        upperCI: mleResult.confidenceIntervals?.scale.upper || 
                 mleResult.parameters.scale + 1.96 * scaleStdErr
      },
      covariance: {
        matrix: covMatrix,
        correlation
      },
      method,
      confidenceLevel,
      sampleSize: 0 // Will be set by the caller if known
    };
  } catch (error) {
    console.error('Error extracting parameter uncertainty:', error);
    return null;
  }
}

/**
 * Cholesky decomposition for 2x2 matrix
 * Used for generating correlated random samples
 */
export function choleskyDecomposition2x2(matrix: number[][]): number[][] {
  const [[a, b], [_c, d]] = matrix;
  
  // Check if matrix is positive definite
  if (a <= 0) {
    throw new Error('Matrix is not positive definite');
  }
  
  const L11 = Math.sqrt(a);
  const L21 = b / L11;
  const L22 = Math.sqrt(d - L21 * L21);
  
  if (isNaN(L22) || L22 <= 0) {
    throw new Error('Matrix is not positive definite');
  }
  
  return [
    [L11, 0],
    [L21, L22]
  ];
}

/**
 * Generate random sample from standard normal distribution
 * Using Box-Muller transform
 */
export function randomNormal(mean: number = 0, stdDev: number = 1): number {
  const u1 = Math.random();
  const u2 = Math.random();
  
  const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return z0 * stdDev + mean;
}

/**
 * Sample Weibull parameters from their joint distribution
 * 
 * @param uncertainty - Parameter uncertainty information
 * @param numSamples - Number of parameter sets to sample
 * @returns Array of sampled parameter pairs
 */
export function sampleWeibullParameters(
  uncertainty: WeibullParameterUncertainty,
  numSamples: number = 1000
): Array<{shape: number, scale: number}> {
  const samples: Array<{shape: number, scale: number}> = [];
  
  try {
    // Use Cholesky decomposition for correlated sampling
    const L = choleskyDecomposition2x2(uncertainty.covariance.matrix);
    
    let validSamples = 0;
    let attempts = 0;
    const maxAttempts = numSamples * 10; // Prevent infinite loop
    
    while (validSamples < numSamples && attempts < maxAttempts) {
      attempts++;
      
      // Generate independent standard normal variates
      const z1 = randomNormal();
      const z2 = randomNormal();
      
      // Transform to correlated parameters
      const shapeSample = uncertainty.shape.estimate + (L[0][0] * z1);
      const scaleSample = uncertainty.scale.estimate + (L[1][0] * z1 + L[1][1] * z2);
      
      // Ensure positive parameters and reasonable bounds
      if (shapeSample > 0.1 && shapeSample < 20 && 
          scaleSample > 1 && scaleSample < 1000) {
        samples.push({
          shape: shapeSample,
          scale: scaleSample
        });
        validSamples++;
      }
    }
    
    if (validSamples < numSamples) {
      console.warn(`Only generated ${validSamples} valid samples out of ${numSamples} requested`);
    }
    
  } catch (error) {
    console.error('Error in Cholesky decomposition, falling back to independent sampling:', error);
    
    // Fallback: Sample parameters independently
    for (let i = 0; i < numSamples; i++) {
      const shapeSample = randomNormal(
        uncertainty.shape.estimate,
        uncertainty.shape.standardError
      );
      const scaleSample = randomNormal(
        uncertainty.scale.estimate,
        uncertainty.scale.standardError
      );
      
      if (shapeSample > 0.1 && scaleSample > 1) {
        samples.push({
          shape: shapeSample,
          scale: scaleSample
        });
      }
    }
  }
  
  return samples;
}

/**
 * Calculate confidence bounds using parameter uncertainty
 * 
 * @param pointEstimate - Point estimate of failures
 * @param parameterUncertainty - Uncertainty in Weibull parameters
 * @param forecastHorizon - How far into the future (affects uncertainty growth)
 * @returns Confidence bounds
 */
export function calculateParameterUncertaintyBounds(
  pointEstimate: number,
  parameterUncertainty: WeibullParameterUncertainty,
  forecastHorizon: number // in years
): { lower: number; upper: number; standardError: number } {
  // Simplified approach: uncertainty grows with forecast horizon
  // This is a placeholder - the actual calculation will be in Phase 2
  
  const baseCV = 0.2; // Base coefficient of variation from parameter uncertainty
  const horizonFactor = 1 + forecastHorizon * 0.1; // Uncertainty grows with time
  
  const standardError = pointEstimate * baseCV * horizonFactor;
  const z = 1.96; // 95% confidence
  
  return {
    lower: Math.max(0, pointEstimate - z * standardError),
    upper: pointEstimate + z * standardError,
    standardError
  };
}

/**
 * Validate parameter uncertainty for use in forecasting
 */
export function validateParameterUncertainty(
  uncertainty: WeibullParameterUncertainty | null
): { isValid: boolean; warnings: string[] } {
  const warnings: string[] = [];
  
  if (!uncertainty) {
    return { isValid: false, warnings: ['No parameter uncertainty information available'] };
  }
  
  // Check correlation
  if (Math.abs(uncertainty.covariance.correlation) > 0.95) {
    warnings.push('Very high correlation between parameters may affect forecast accuracy');
  }
  
  // Check relative uncertainty
  const shapeCV = uncertainty.shape.standardError / uncertainty.shape.estimate;
  const scaleCV = uncertainty.scale.standardError / uncertainty.scale.estimate;
  
  if (shapeCV > 0.5) {
    warnings.push(`High uncertainty in shape parameter (CV=${(shapeCV * 100).toFixed(1)}%)`);
  }
  
  if (scaleCV > 0.5) {
    warnings.push(`High uncertainty in scale parameter (CV=${(scaleCV * 100).toFixed(1)}%)`);
  }
  
  // Check if confidence intervals are reasonable
  const shapeRange = uncertainty.shape.upperCI - uncertainty.shape.lowerCI;
  const scaleRange = uncertainty.scale.upperCI - uncertainty.scale.lowerCI;
  
  if (shapeRange > uncertainty.shape.estimate * 2) {
    warnings.push('Shape parameter confidence interval is very wide');
  }
  
  if (scaleRange > uncertainty.scale.estimate * 2) {
    warnings.push('Scale parameter confidence interval is very wide');
  }
  
  return {
    isValid: true,
    warnings
  };
}
